import { CSSProperties, ReactElement, ReactNode, RefObject, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface DropdownPortalProps {
  // Element the dropdown is positioned against (usually the trigger button's wrapper).
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  // When true the dropdown's RIGHT edge is inset from the anchor's right edge;
  // otherwise its LEFT edge is offset from the anchor's left edge.
  alignRight?: boolean;
  // Horizontal inset in px from the chosen anchor edge.
  offsetX?: number;
  // Gap in px below the anchor.
  offsetY?: number;
  children: ReactNode;
}

/**
 * Renders dropdown/popover content into document.body via a portal so it is
 * always painted above in-page geometry (sticky bars, cr-z-* stacking
 * contexts, overflow containers). Positioned fixed against the anchor and
 * re-anchored on scroll/resize while open.
 */
export default function DropdownPortal(props: DropdownPortalProps): ReactElement | null {
  const { anchorRef, open, alignRight = false, offsetX = 0, offsetY = 4, children } = props;
  const [style, setStyle] = useState<CSSProperties | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return undefined;
    }
    const update = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const next: CSSProperties = { position: 'fixed', top: rect.bottom + offsetY };
      if (alignRight) {
        next.right = window.innerWidth - rect.right + offsetX;
      } else {
        next.left = rect.left + offsetX;
      }
      setStyle(next);
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef, alignRight, offsetX, offsetY]);

  if (!open || !style) return null;

  return createPortal(
    <div className="cr-z-[5000]" style={style}>
      {children}
    </div>,
    document.body
  );
}
